<script setup lang="ts">
import type {
  DocumentDownloadFormat,
  DocumentItem,
  LibraryListResponse,
} from '@/js/account/entities/document';
import { computed, nextTick, ref } from 'vue';
import BasePagination from '@/js/account/components/base/BasePagination.vue';
import LibrarySearchDocumentGroup from '@/js/account/components/library/search/LibrarySearchDocumentGroup.vue';
import LibrarySearchSnippet from '@/js/account/components/library/search/LibrarySearchSnippet.vue';
import LibrarySearchViewToggle from '@/js/account/components/library/search/LibrarySearchViewToggle.vue';
import { useLibrarySearchResults } from '@/js/account/entities/document';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    list: LibraryListResponse
    downloadingId: number | null
    deletingId?: number | null
    showAdminActions?: boolean
  }>(),
  { deletingId: null, showAdminActions: false },
);

const emit = defineEmits<{
  download: [document: DocumentItem, format: DocumentDownloadFormat]
  delete: [document: DocumentItem]
}>();

const cn = classNames('library-search-results');

const {
  meta,
  mode,
  page,
  perPage,
  total,
  groups,
  pagedGroups,
  pagedMatches,
  isOpen,
  toggleGroup,
  isExpanded,
  expandGroup,
  setMode,
  setPage,
} = useLibrarySearchResults(
  computed<LibraryListResponse | null>(() => props.list),
);

const summaryRef = ref<HTMLElement | null>(null);

const summary = computed(() => {
  const current = meta.value;

  if (!current) {
    return '';
  }

  const matches = current.matchesTotal;
  const documents = groups.value.length;
  const matchNoun = matches === 1 ? 'match' : 'matches';
  const documentNoun = documents === 1 ? 'document' : 'documents';
  const prefix = current.truncated ? 'First ' : '';

  return `${prefix}${matches} ${matchNoun} in ${documents} ${documentNoun}`;
});

async function onPageChange(next: number): Promise<void> {
  setPage(next);
  await nextTick();
  summaryRef.value?.focus();
}
</script>

<template>
  <div :class="cn()">
    <div :class="cn('head')">
      <p
        ref="summaryRef"
        :class="cn('summary')"
        tabindex="-1"
        aria-live="polite"
      >
        {{ summary }}
      </p>
      <LibrarySearchViewToggle
        :model-value="mode"
        @update:model-value="setMode"
      />
    </div>

    <div v-if="mode === 'grouped'" :class="cn('groups')">
      <LibrarySearchDocumentGroup
        v-for="group in pagedGroups"
        :key="group.document.id"
        :group="group"
        :open="isOpen(group.document.id)"
        :expanded="isExpanded(group.document.id)"
        :downloading="downloadingId === group.document.id"
        :deleting="deletingId === group.document.id"
        :show-admin-actions="showAdminActions"
        @toggle="toggleGroup(group.document.id)"
        @expand="expandGroup(group.document.id)"
        @download="emit('download', group.document, $event)"
        @delete="emit('delete', group.document)"
      />
    </div>
    <ol v-else :class="cn('matches')">
      <LibrarySearchSnippet
        v-for="match in pagedMatches"
        :key="match.key"
        :match="match"
        :number="match.number"
        show-document-title
      />
    </ol>

    <BasePagination
      :page="page"
      :total="total"
      :per-page="perPage"
      :class="cn('pagination')"
      @update:page="onPageChange"
    />
  </div>
</template>

<style lang="scss" scoped>
.library-search-results {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__summary {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: 14px;

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 4px;
    }
  }

  &__groups {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__matches {
    margin: 0;
    padding: 0;
    border-radius: 4px;
    background-color: var(--color-white);
    box-shadow: var(--shadow);
    list-style: none;
    overflow: hidden;

    :deep(.library-search-snippet:first-child) {
      border-top: none;
    }
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: 8px;
  }

  &__hint {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: 14px;
  }

  @media (max-width: 768px) {
    &__head {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
